import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import {
  Cryptocurrency,
  Currency,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { withFragments } from '#src/lib/gql/index.ts';
import { toLamport } from '#src/lib/lamport.ts';
import type { MonetaryAmountParams } from '#src/lib/monetaryAmount.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';
import { toWei } from '#src/lib/wei.ts';

import type { useMonetaryAmount_monetaryAmount } from './__generated__/useMonetaryAmount.graphql.ts';

export const Sum = (amounts: MonetaryAmount[]) => {
  return amounts.reduce((acc, amount) => {
    return new MonetaryAmount({
      eurCents: acc.eurCents + amount.eurCents,
      usdCents: acc.usdCents + amount.usdCents,
      gbpCents: acc.gbpCents + amount.gbpCents,
      wei: acc.wei + amount.wei,
      lamport: acc.lamport + amount.lamport,
      referenceCurrency: acc.referenceCurrency,
    });
  }, MonetaryAmount.zero);
};

export const useMonetaryAmount = withFragments(
  () => {
    const { exchangeRate } = useConfigContext();
    const { currency, cryptocurrency, fiatCurrency } = useCurrentUserContext();

    const toMonetaryAmount = useCallback(
      (params: MonetaryAmountParams | null | undefined): MonetaryAmount => {
        if (params === null || params === undefined) {
          return MonetaryAmount.zero;
        }
        if (params instanceof MonetaryAmount) {
          return params;
        }
        return MonetaryAmount.fromRates(params, exchangeRate.ethRates);
      },
      [exchangeRate.ethRates]
    );

    const toMonetaryAmountInReferenceCurrency = useCallback(
      (
        amount: number | bigint | string,
        referenceCurrency: SupportedCurrency
      ): MonetaryAmount => {
        return MonetaryAmount.fromReferenceCurrency(
          referenceCurrency,
          amount,
          exchangeRate.ethRates
        );
      },
      [exchangeRate.ethRates]
    );

    const eur = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.EUR
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const usd = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.USD
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const gbp = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          amount,
          SupportedCurrency.GBP
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const wei = useCallback(
      (amount: bigint | number | string): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          BigInt(amount),
          SupportedCurrency.WEI
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const lamport = useCallback(
      (amount: bigint | number | string): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(
          BigInt(amount),
          SupportedCurrency.LAMPORT
        );
      },
      [toMonetaryAmountInReferenceCurrency]
    );

    const toMonetaryAmountInFiatCurrency = useCallback(
      (amount: number): MonetaryAmount => {
        return toMonetaryAmountInReferenceCurrency(amount, fiatCurrency);
      },
      [toMonetaryAmountInReferenceCurrency, fiatCurrency]
    );

    const toMonetaryAmountFromMajorMainCurrency = useCallback(
      (amountInMajorUnit: number): MonetaryAmount => {
        if (currency === Currency.FIAT) {
          return toMonetaryAmountInReferenceCurrency(
            amountInMajorUnit * 100,
            fiatCurrency
          );
        }

        if (cryptocurrency === Cryptocurrency.LAMPORT) {
          return lamport(toLamport(amountInMajorUnit));
        }
        return wei(toWei(amountInMajorUnit));
      },
      [
        currency,
        cryptocurrency,
        wei,
        toMonetaryAmountInReferenceCurrency,
        fiatCurrency,
        lamport,
      ]
    );

    const getUserFiatAmountInMajorUnit = useCallback(
      (monetaryAmount: MonetaryAmount) =>
        monetaryAmount.toMajorUnit(fiatCurrency),
      [fiatCurrency]
    );

    const getUserCryptoAmountInSubUnit = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        return monetaryAmount.cryptoSubUnit(cryptocurrency);
      },
      [cryptocurrency]
    );

    const getUserCryptoAmountInMajorUnit = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        return monetaryAmount.crypto(cryptocurrency);
      },
      [cryptocurrency]
    );

    const getUserAmount = useCallback(
      (monetaryAmount: MonetaryAmount) => {
        if (currency === Currency.FIAT) {
          return getUserFiatAmountInMajorUnit(monetaryAmount);
        }
        return getUserCryptoAmountInMajorUnit(monetaryAmount);
      },
      [currency, getUserFiatAmountInMajorUnit, getUserCryptoAmountInMajorUnit]
    );

    return {
      toMonetaryAmount,
      toMonetaryAmountInFiatCurrency,
      toMonetaryAmountFromMajorMainCurrency,
      getUserFiatAmountInMajorUnit,
      getUserCryptoAmountInMajorUnit,
      getUserCryptoAmountInSubUnit,
      getUserAmount,
      toMonetaryAmountInReferenceCurrency,
      eur,
      usd,
      gbp,
      wei,
      lamport,
    };
  },
  {
    monetaryAmount: gql`
      fragment useMonetaryAmount_monetaryAmount on MonetaryAmount {
        eurCents
        usdCents
        gbpCents
        wei
        lamport
        referenceCurrency
      }
    ` as TypedDocumentNode<useMonetaryAmount_monetaryAmount>,
  }
);
